import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { apiRequest } from '../lib/api-client';
import { formatDateTime } from '../ui/format';
import { Badge, ErrorState, Loading, Panel, type BadgeTone } from '../ui/Page';
import type { AnnouncementKind } from '../app/StatusBell';

/** Writing to every tenant at once: releases, notices, incidents, maintenance. */

interface Row {
  id: string;
  kind: AnnouncementKind;
  title: string;
  body: string;
  version: string | null;
  publishedAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

const KINDS: AnnouncementKind[] = ['RELEASE', 'NOTICE', 'INCIDENT', 'MAINTENANCE'];
const tones: Record<AnnouncementKind, BadgeTone> = {
  RELEASE: 'info',
  NOTICE: 'neutral',
  INCIDENT: 'danger',
  MAINTENANCE: 'warning',
};

export function AnnouncementsPanel(): React.JSX.Element {
  const client = useQueryClient();
  const list = useQuery({
    queryKey: ['admin-announcements'],
    queryFn: () => apiRequest<{ announcements: Row[] }>('/admin/announcements'),
  });

  const [kind, setKind] = useState<AnnouncementKind>('RELEASE');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [version, setVersion] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['admin-announcements'] });
    void client.invalidateQueries({ queryKey: ['announcements'] });
    void client.invalidateQueries({ queryKey: ['platform-status'] });
  };

  const create = useMutation({
    mutationFn: (publish: boolean) =>
      apiRequest('/admin/announcements', {
        method: 'POST',
        body: { kind, title: title.trim(), body, version: version.trim() || null, publish },
      }),
    onSuccess: () => {
      setTitle('');
      setBody('');
      setVersion('');
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const patch = useMutation({
    mutationFn: (input: { id: string; publish?: boolean; resolve?: boolean }) =>
      apiRequest(`/admin/announcements/${input.id}`, {
        method: 'PATCH',
        body: { publish: input.publish, resolve: input.resolve },
      }),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiRequest(`/admin/announcements/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <div className="stack">
      <Panel title={t('admin.announce.new')}>
        <div className="form-grid">
          <label className="stack__field">
            <span>{t('admin.announce.kind')}</span>
            <select
              className="select"
              value={kind}
              onChange={(event) => setKind(event.target.value as AnnouncementKind)}
            >
              {KINDS.map((key) => (
                <option key={key} value={key}>
                  {t(`status.kind.${key}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="stack__field">
            <span>{t('admin.announce.version')}</span>
            <input
              value={version}
              maxLength={40}
              placeholder="v1.4.0"
              onChange={(event) => setVersion(event.target.value)}
            />
          </label>
        </div>
        <label className="stack__field">
          <span>{t('admin.announce.title')}</span>
          <input value={title} maxLength={160} onChange={(event) => setTitle(event.target.value)} />
        </label>
        <label className="stack__field">
          <span>{t('admin.announce.body')}</span>
          <textarea
            rows={5}
            value={body}
            maxLength={8000}
            onChange={(event) => setBody(event.target.value)}
          />
        </label>
        {error && (
          <p className="alert small" role="alert">
            {error}
          </p>
        )}
        <div className="cell-inline">
          <button
            type="button"
            className="button button--compact"
            disabled={!title.trim() || create.isPending}
            onClick={() => create.mutate(false)}
          >
            {t('admin.announce.saveDraft')}
          </button>
          <button
            type="button"
            className="button button--primary button--compact"
            disabled={!title.trim() || create.isPending}
            onClick={() => create.mutate(true)}
          >
            {t('admin.announce.publish')}
          </button>
        </div>
      </Panel>

      <Panel title={t('admin.announce.list')}>
        {list.isLoading ? (
          <Loading />
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : (list.data?.announcements.length ?? 0) === 0 ? (
          <p className="muted">{t('admin.announce.none')}</p>
        ) : (
          <ul className="social-accounts">
            {list.data!.announcements.map((row) => (
              <li key={row.id} className="social-account">
                <span className="cell-stack">
                  <span className="cell-inline">
                    <Badge tone={row.resolvedAt ? 'neutral' : tones[row.kind]}>
                      {t(`status.kind.${row.kind}`)}
                      {row.version ? ` ${row.version}` : ''}
                    </Badge>
                    <strong>{row.title}</strong>
                  </span>
                  <span className="muted small">
                    {row.publishedAt
                      ? `${t('admin.announce.published')} ${formatDateTime(row.publishedAt)}`
                      : t('admin.announce.draft')}
                    {row.resolvedAt ? ` · ${t('status.resolved')}` : ''}
                  </span>
                </span>
                <span className="cell-inline">
                  {!row.publishedAt && (
                    <button
                      type="button"
                      className="button button--primary button--compact"
                      onClick={() => patch.mutate({ id: row.id, publish: true })}
                    >
                      {t('admin.announce.publish')}
                    </button>
                  )}
                  {(row.kind === 'INCIDENT' || row.kind === 'MAINTENANCE') &&
                    row.publishedAt &&
                    !row.resolvedAt && (
                      <button
                        type="button"
                        className="button button--compact"
                        onClick={() => patch.mutate({ id: row.id, resolve: true })}
                      >
                        {t('admin.announce.resolve')}
                      </button>
                    )}
                  <button
                    type="button"
                    className="button button--quiet button--danger button--compact"
                    onClick={() => remove.mutate(row.id)}
                  >
                    {t('social.delete')}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
